import axiosInstance from "./api";

/**
 * Fetch a single published blog by its slug.
 * Returns the blog object (including markdown `content` and `related[]` array).
 *
 * @param {string} slug - URL slug of the blog post
 * @returns {Promise<{blog: object}>}
 */
export const getBlogBySlug = async (slug) => {
  try {
    const response = await axiosInstance.get(`/blogs/${slug}`);
    return response.data; // { success, blog: { ...fields, related[] } }
  } catch (error) {
    const data = error?.response?.data;
    const status = error?.response?.status;
    let message = "An error occurred while fetching the blog.";

    if (status === 404) {
      message = "Blog not found.";
    } else if (data?.error) {
      message = data.error;
    }

    throw new Error(message);
  }
};

/**
 * Fetch a paginated list of published blogs.
 * Supports optional `category` and `tag` filters.
 *
 * @param {{ page?: number, limit?: number, category?: string, tag?: string }} params
 * @returns {Promise<{ blogs: object[], total: number, page: number, pages: number }>}
 */
export const getAllBlogs = async ({
  page = 1,
  limit = 10,
  category,
  tag,
} = {}) => {
  try {
    const params = { page, limit };
    if (category) params.category = category;
    if (tag) params.tag = tag;

    const response = await axiosInstance.get("/blogs", { params });
    return response.data; // { success, total, page, pages, blogs[] }
  } catch (error) {
    const data = error?.response?.data;
    let message = "An error occurred while fetching blogs.";

    if (data?.error) {
      message = data.error;
    }

    throw new Error(message);
  }
};

/**
 * Seed a blog with form data and a secret key.
 *
 * @param {FormData} formData - Form data containing blog details and files
 * @param {string} secretKey - Secret key to authorize seeding
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const seedBlog = async (blogData, secretKey) => {
  try {
    const response = await axiosInstance.post("/blogs/seed", blogData, {
      headers: {
        "x-blog-seed-secret-key": secretKey,
      },
    });
    return response.data;
  } catch (error) {
    const data = error?.response?.data;
    let message = "An error occurred while seeding the blog.";

    if (data?.error) {
      message = data.error;
    }

    throw new Error(message);
  }
};
